/**
 * Shared "Replace" wildcard placeholder logic.
 *
 * A wildcard is a {{ }} token whose inner text is the reserved keyword
 * `replace`, optionally followed by `.hint` and pipe-delimited choices, e.g.
 *   {{replace}}
 *   {{replace.clinical impression}}
 *   {{replace.Hearing Loss Type | Sensorineural | Conductive | Mixed}}
 *
 * Wildcards are never resolved against patient/clinic data. This module is the
 * single source of truth for classifying, parsing and locating them so every
 * consumer of {{ }} tokens (resolver, highlight, completion/signing scan) stays
 * in agreement.
 */

export const REPLACE_WILDCARD_KEYWORD = "replace";
export const REPLACE_WILDCARD_OPTIONS_DELIMITER = "|";
export const REPLACE_WILDCARD_CLASS = "replace-wildcard";
export const REPLACE_TAG_BUILDER_VALUE = "replace.__builder__";
export const REPLACE_TAG_BUILDER_TOKEN = `{{${REPLACE_TAG_BUILDER_VALUE}}}`;
export const REPLACE_TAG_DEFAULT_VALUE_COUNT = 3;
export const REPLACE_TAG_MAX_VALUE_COUNT = 10;

export interface ParsedWildcard {
  /** Full token as written, e.g. "{{replace.clinical impression}}". */
  raw: string;
  /** Human-readable hint (text after the dot, before the first delimiter). */
  hint: string;
  /** Pipe-delimited choices, empty for a free-text wildcard. */
  options: string[];
}

const stripBraces = (tag: string): string => {
  const t = tag.trim();
  return t.startsWith("{{") && t.endsWith("}}") ? t.slice(2, -2) : t;
};

/** True when the inner text of a {{ }} token is a Replace wildcard. */
export const isWildcardInner = (inner: string): boolean => {
  const trimmed = inner.trim().toLowerCase();
  return (
    trimmed === REPLACE_WILDCARD_KEYWORD ||
    trimmed.startsWith(`${REPLACE_WILDCARD_KEYWORD}.`)
  );
};

/** True for a full token "{{...}}" or bare inner text "...". */
export const isWildcardTag = (tag: string): boolean =>
  isWildcardInner(stripBraces(tag));

export const buildReplaceWildcardToken = (
  hint: string,
  values: string[] = []
): string => {
  const cleanedHint = hint.trim();
  const cleanedValues = values.map((value) => value.trim()).filter(Boolean);
  return `{{${REPLACE_WILDCARD_KEYWORD}.${[
    cleanedHint,
    ...cleanedValues
  ].join(` ${REPLACE_WILDCARD_OPTIONS_DELIMITER} `)}}}`;
};

export const parseWildcardTag = (tag: string): ParsedWildcard | null => {
  const inner = stripBraces(tag).trim();
  if (!isWildcardInner(inner)) return null;

  const raw = `{{${inner}}}`;

  // Drop the reserved keyword (and the dot separator, if present).
  const afterKeyword = inner.slice(REPLACE_WILDCARD_KEYWORD.length);
  const hintAndOptions = afterKeyword.startsWith(".")
    ? afterKeyword.slice(1)
    : "";

  const segments = hintAndOptions
    .split(REPLACE_WILDCARD_OPTIONS_DELIMITER)
    .map((segment) => segment.trim());

  const hint = segments[0] ?? "";
  const options = segments.slice(1).filter(Boolean);

  return { raw, hint, options };
};

const WILDCARD_SCAN_REGEXP = /\{\{(.*?)\}\}/g;
const WILDCARD_TOKEN_REGEXP = /\{\{\s*replace(\.[^{}]*)?\s*\}\}/gi;

const escapeHtml = (value: string): string =>
  value
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;");

export const getWildcardHighlightHtml = (rawToken: string): string => {
  const parsed = parseWildcardTag(rawToken);
  const title = parsed?.hint
    ? `Replace placeholder: ${parsed.hint}`
    : "Replace placeholder — fill in before completing";

  return `<span class="${REPLACE_WILDCARD_CLASS}" data-replace-wildcard="1" contenteditable="false" title="${escapeHtml(title)}">${escapeHtml(rawToken)}</span>`;
};

export const decorateWildcardTokensInHtml = (html: string): string => {
  if (
    !html ||
    html.indexOf("{{") < 0 ||
    html.includes(`class="${REPLACE_WILDCARD_CLASS}`) ||
    html.includes(`class='${REPLACE_WILDCARD_CLASS}`)
  ) {
    return html;
  }

  return html.replace(WILDCARD_TOKEN_REGEXP, (rawToken) =>
    getWildcardHighlightHtml(rawToken)
  );
};

/**
 * Find every unresolved Replace wildcard in a string of HTML/text. Works on raw
 * tokens whether or not they are wrapped in highlight markup, since the literal
 * "{{replace...}}" is preserved inside the highlight span.
 */
export const findUnresolvedWildcards = (html: string): ParsedWildcard[] => {
  if (!html || html.indexOf("{{") < 0) return [];

  const result: ParsedWildcard[] = [];
  let match: RegExpExecArray | null;
  WILDCARD_SCAN_REGEXP.lastIndex = 0;

  while ((match = WILDCARD_SCAN_REGEXP.exec(html)) !== null) {
    if (match.index === WILDCARD_SCAN_REGEXP.lastIndex) {
      WILDCARD_SCAN_REGEXP.lastIndex++;
    }
    if (isWildcardInner(match[1])) {
      const parsed = parseWildcardTag(match[0]);
      if (parsed) result.push(parsed);
    }
  }

  return result;
};

export const hasUnresolvedWildcards = (html: string): boolean =>
  findUnresolvedWildcards(html).length > 0;
